import { FC, useCallback } from 'react'
import { Stack } from '@mui/material'
import { patternFormatter } from 'react-number-format'

import { SidePanelElementProps } from '@clara/layout-next'
import { useT } from '@clara/locale'
import { get } from '@clara/utils'
import { useAsyncFn, useEffectOnce } from '@clara/hooks'

import { getBanksService } from '@/features/reimbursements/api/get-bank-name'
import { CpfMask, BrPhoneMask } from '@/utils/string'
import { BankInfoResponse } from '../../api/bank-info'
import { RowItem } from './row-item'

type Props = {
  bankInfo?: BankInfoResponse
  view?: 'FULL' | 'SHORT'
}

const infoPerMethod = {
  TED: [
    'name',
    'accountType',
    'cpf',
    'branchCode',
    'accountNumber',
    'accountSubType',
    'bankKey',
  ],
  PIX: ['name', 'accountType', 'accountNumber'],
}

const blacklist = ['name']

export const TedPixDetails: FC<SidePanelElementProps & Props> = ({
  bankInfo,
  view = 'FULL',
}) => {
  const { t } = useT('reimbursements')

  const [banksResponse, getBanks] = useAsyncFn(async () => {
    return await getBanksService({ countryCode: 'BR' })
  }, [])

  useEffectOnce(() => {
    getBanks()
  })

  const paymentMethod = bankInfo?.accountType

  const getValue = useCallback(
    (item: any) => {
      if (!bankInfo) {
        return '-'
      }
      switch (item) {
        case 'cpf':
          if (bankInfo.cpf) {
            return patternFormatter(bankInfo.cpf, {
              format: CpfMask,
            })
          }
          return '-'
        case 'accountNumber':
          if (bankInfo.accountType === 'PIX') {
            if (bankInfo.accountSubType === 'CPF') {
              return patternFormatter(bankInfo.accountNumber, {
                format: CpfMask,
              })
            }
            if (bankInfo.accountSubType === 'PHONE') {
              return patternFormatter(bankInfo.accountNumber, {
                format: BrPhoneMask,
              })
            }
          }
          return get(bankInfo, item)
        case 'accountSubType':
          return t(`list.${bankInfo.accountSubType}`)
        case 'bankKey':
          return (
            banksResponse.value?.find((bank) => bank.key === bankInfo.bankKey)
              ?.name ?? '-'
          )
        default:
          return get(bankInfo, item)
      }
    },
    [bankInfo, banksResponse.value, t],
  )
  const getTitle = useCallback(
    (item: string) => {
      if (!bankInfo) {
        return '-'
      }
      switch (item) {
        case 'accountNumber':
          return t(`list.${bankInfo.accountSubType}`)
        default:
          return t(`list.${item}`)
      }
    },
    [bankInfo, t],
  )

  return (
    <Stack>
      {paymentMethod && bankInfo ? (
        <Stack>
          {infoPerMethod[paymentMethod].map((item: any) =>
            view === 'SHORT' && blacklist.includes(item) ? null : (
              <RowItem
                key={`pix-details-${item}`}
                title={getTitle(item)}
                value={getValue(item)}
              />
            ),
          )}
        </Stack>
      ) : null}
    </Stack>
  )
}
